import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatChipsModule} from '@angular/material/chips';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {ApiDeviceGroupRequestDto, ApiDeviceGroupResponseDto} from '@birco/rainmesh-api';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {combineLatest, map, startWith} from 'rxjs';
import {InputWrapperComponent, isSubmitAllowed} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {DeviceActions} from '../../state/device.actions';
import {selectAllDevices} from '../../state/device.selectors';
import { TagComponent } from "../../../../shared/components/tag/tag.component";

type DeviceGroupDialogData = {
  mode: 'create' | 'update';
  deviceGroup?: ApiDeviceGroupResponseDto;
};

export type DeviceGroupDialogResult = {
  id?: string;
  requestDto: ApiDeviceGroupRequestDto;
};

@Component({
  imports: [
    AsyncPipe,
    MatDialogModule,
    TranslatePipe,
    ReactiveFormsModule,
    InputWrapperComponent,
    MatAutocompleteModule,
    MatChipsModule,
    IconComponent,
    TagComponent
],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dialog-update-device-group.component.html',
  styleUrl: './dialog-update-device-group.component.scss',
})
export class DialogUpdateDeviceGroupComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);

  data = inject<DeviceGroupDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DialogUpdateDeviceGroupComponent>);

  readonly form: FormGroup = this.fb.group({
    name: [this.data?.deviceGroup?.name ?? '', Validators.required],
    deviceIds: [this.data?.deviceGroup?.devices?.map((device) => device.id) ?? [], Validators.required],
    deviceSearch: [''],
  });

  private readonly selectableDevices$ = this.store.select(selectAllDevices).pipe(
    map((devices) => devices
      .slice()
      .sort((left, right) => {
        if (left.isOutOfService !== right.isOutOfService) {
          return left.isOutOfService ? 1 : -1;
        }
        return left.name.localeCompare(right.name);
      })),
  );

  readonly selectedDevices$ = combineLatest([
    this.selectableDevices$,
    this.form.controls['deviceIds'].valueChanges.pipe(startWith(this.form.controls['deviceIds'].value ?? [])),
  ]).pipe(
    map(([devices, selectedIds]) => {
      const selectedIdSet = new Set((selectedIds ?? []) as string[]);
      return devices.filter((device) => selectedIdSet.has(device.id));
    }),
  );

  readonly availableDevices$ = combineLatest([
    this.selectableDevices$,
    this.form.controls['deviceSearch'].valueChanges.pipe(startWith(this.form.controls['deviceSearch'].value ?? '')),
    this.form.controls['deviceIds'].valueChanges.pipe(startWith(this.form.controls['deviceIds'].value ?? [])),
  ]).pipe(
    map(([devices, search, selectedIds]) => {
      const searchTerm = String(search ?? '').trim().toLowerCase();
      const selectedIdSet = new Set((selectedIds ?? []) as string[]);

      return devices
        .filter((device) => !selectedIdSet.has(device.id))
        .filter((device) => searchTerm.length === 0 || device.name.toLowerCase().includes(searchTerm));
    }),
  );

  constructor() {
    this.store.dispatch(DeviceActions.public.loadAllDevicesByFilters({filter: {}}));
  }

  get titleKey(): string {
    return this.data.mode === 'create'
      ? 'deviceGroups.form.title.create'
      : 'deviceGroups.form.title.edit';
  }

  onSubmit() {
    if (!isSubmitAllowed(this.form)) {
      return;
    }

    const payload: ApiDeviceGroupRequestDto = {
      name: String(this.form.value.name ?? '').trim(),
      deviceIds: (this.form.value.deviceIds ?? []) as string[],
    };

    this.dialogRef.close({
      id: this.data.deviceGroup?.id,
      requestDto: payload,
    } satisfies DeviceGroupDialogResult);
  }

  addDevice(id: string) {
    const current = (this.form.controls['deviceIds'].value ?? []) as string[];
    const next = Array.from(new Set([...current, id]));

    this.form.controls['deviceIds'].setValue(next);
    this.form.controls['deviceIds'].markAsDirty();
    this.form.controls['deviceIds'].updateValueAndValidity();
    this.form.controls['deviceSearch'].setValue('');
  }

  removeDevice(id: string) {
    const current = (this.form.controls['deviceIds'].value ?? []) as string[];
    const next = current.filter((currentId) => currentId !== id);

    this.form.controls['deviceIds'].setValue(next);
    this.form.controls['deviceIds'].markAsDirty();
    this.form.controls['deviceIds'].updateValueAndValidity();
  }
}
